<template>
  <div>
    <div class="tw-flex tw-items-start tw-mb-6">
      <base-select
        label="Pricing"
        placeholder="Select a pricing"
        :options="customProgramGroups"
        label-key="label"
        value-key="value"
        :value="selectedProgramGroup"
        @input="onProgramGroupChange"
        :disabled="!programs.length"
        class="tw-flex-grow tw-mr-4"
      />

      <base-select
        label="Program Category"
        placeholder="Select a category"
        :options="programCategories"
        label-key="label"
        value-key="value"
        :value="selectedProgramCategory"
        @input="onProgramCategoryChange"
        :disabled="!programs.length"
        class="tw-flex-grow"
      />
    </div>

    <div class="tw-flex tw-items-start">
      <base-select
        label="Program"
        placeholder="Select a program"
        :optgroups="sortedPrograms"
        label-key="name"
        value-key="businessProgramId"
        :value="value.businessProgramId"
        @input="updatePrescriptionProgram"
        :disabled="!programs.length"
        :required="required"
        class="tw-flex-grow"
      />
      <base-icon color="red" :class="['tw-mt-2 tw-ml-2', { 'tw-invisible': !value.businessProgramId }]" @click="updatePrescriptionProgram(null)">
        <x-icon class="tw-w-5 tw-h-5" />
      </base-icon>
    </div>

    <div v-if="selectedProgram" class="tw-pl-6 tw-mt-4">
      <div v-for="medicationIndex in selectedProgram.maxMedications" :key="medicationIndex" class="tw-flex tw-items-start">
        <base-select
          :label="`${formatFieldLabel(selectedProgram.programFieldLabel)} ${medicationIndex}`"
          :placeholder="`Select a ${formatFieldLabel(selectedProgram.programFieldLabel).toLowerCase()}`"
          :optgroups="sortedMedications"
          label-key="name"
          value-key="businessItemCategoryId"
          :value="value.medications[medicationIndex - 1]"
          @input="updatePrescriptionMedications(medicationIndex - 1, $event)"
          class="tw-mt-1 tw-w-full"
          :rules="[(med) => value.medications.filter((m) => med === m).length <= 1 || 'Remove duplicates']"
        />
        <base-icon
          color="red"
          :class="['tw-mt-2.5 tw-ml-2', { 'tw-invisible': !value.medications[medicationIndex - 1] }]"
          @click="updatePrescriptionMedications(medicationIndex - 1, null)"
        >
          <x-icon class="tw-w-5 tw-h-5" />
        </base-icon>
      </div>
      <prescription-rx-item-input
        v-if="filteredRxItems.length > 0"
        :value="value"
        :allRxItems="filteredRxItems"
        class="tw-mt-4"
        ref="prescriptionRxItemInput"
        @input="$emit('input', $event)"
      />

      <base-textarea class="tw-mt-6 tw-mb-5 tw-mr-9" label="Program Notes" placeholder="Enter program notes" :value="value.notes" @input="updatePrescriptionNotes" />
    </div>
  </div>
</template>

<script>
import { XIcon } from "@/assets/svgs";
import { cloneDeep, uniq, orderBy } from "lodash";
import PrescriptionRxItemInput from "@/views/business/components/PrescriptionRxItemInput";

export default {
  name: "FilterProgramComponent",
  components: {
    XIcon,
    PrescriptionRxItemInput,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    programs: {
      type: Array,
      required: true,
    },
    programGroups: {
      type: Array,
      required: true,
    },
    allRxItems: {
      type: Array,
      default: () => [],
    },
    required: Boolean,
  },
  data() {
    return {
      customProgramGroups: [],
      programCategories: [],
      selectedProgramGroup: null,
      selectedProgramCategory: null,
      filteredPrograms: [],
    };
  },
  computed: {
    selectedProgram() {
      return this.filteredPrograms.find((p) => p.businessProgramId === this.value.businessProgramId);
    },
    sortedMedications() {
      if (!this.selectedProgram) return [];
      const groups = orderBy(uniq(this.selectedProgram.allowedMedications.map((m) => m.productGroup)));

      return groups.map((g) => ({
        label: g,
        options: orderBy(this.selectedProgram.allowedMedications.filter((m) => m.productGroup === g), ["name"]),
      }));
    },
    sortedPrograms() {
      if (!this.programGroups.length) return [];
      return orderBy(
        this.programGroups
          .filter((pg) => this.filteredPrograms.some((p) => p.programGroup === pg))
          .map((pg) => ({
            label: pg || "No Group",
            options: orderBy(this.filteredPrograms.filter((p) => p.programGroup === pg), ["name"]),
          })),
        ["label"],
      );
    },
    /**
     * Returns an array of Rx Item objects that are visible for selection
     * based on the currently selected program and each Rx Item's own filtering rules.
     */
    filteredRxItems() {
      // If no program is selected, or no allRxItems provided, return an empty array.
      if (!this.selectedProgram || !this.allRxItems || this.allRxItems.length === 0) {
        return [];
      }

      const currentProgramId = this.selectedProgram.businessProgramId;

      const filtered = this.allRxItems.filter((rxItem) => {
        const itemFilterType = rxItem.programFilterType;
        let itemProgramsIds = [];

        // Safely parse the programsId for the current rxItem from JSON string to an array
        if (rxItem.programsId && typeof rxItem.programsId === "string") {
          try {
            itemProgramsIds = JSON.parse(rxItem.programsId);
          } catch (e) {
            itemProgramsIds = []; // Treat as empty array if parsing fails
          }
        } else if (Array.isArray(rxItem.programsId)) {
          itemProgramsIds = rxItem.programsId;
        }

        // Apply filtering logic based on the rxItem's own programFilterType
        let isVisible = false;
        if (itemFilterType === null || itemFilterType === "all") {
          // Rule 1: If rxItem's filter is null or 'all', it's visible for any selected program.
          isVisible = true;
        } else if (itemFilterType === "include") {
          // Rule 2: If rxItem's filter is 'include', it's visible ONLY if
          // the selected program's ID is present in this rxItem's included programs list.
          isVisible = itemProgramsIds.includes(currentProgramId);
        } else if (itemFilterType === "exclude") {
          // Rule 3: If rxItem's filter is 'exclude', it's visible ONLY if
          // the selected program's ID is NOT present in this rxItem's excluded programs list.
          isVisible = !itemProgramsIds.includes(currentProgramId);
        } else {
          isVisible = true; // Fallback for any unknown programFilterType
        }
        return isVisible;
      });
      return filtered;
    },
  },

  created() {
    this.initializeFilters();

    // Check if initial program selection based on `value.businessProgramId` is present
    if (this.value && this.value.businessProgramId) {
      // Flatten the programs prop to find the initial program across all groups
      const initialProgram = this.programs.flatMap((pg) => pg.options || []).find((p) => p.businessProgramId === this.value.businessProgramId);
      if (initialProgram) {
        this.selectedProgramGroup = initialProgram.programGroup;
        this.selectedProgramCategory = initialProgram.programCategory;
        // Ensure filters are applied after setting initial values
        this.filterPrograms();
        this.filterCategories();
      }
    }
  },
  methods: {
    initializeFilters() {
      // Flatten the programs prop to get a single list of all programs
      this.filteredPrograms = this.programs.flatMap((pg) => pg.options || []);
      this.customProgramGroups = this.getGroups(this.programs).sort((a, b) => a.label.localeCompare(b.label));
      this.programCategories = this.getCategories(this.programs).sort((a, b) => a.label.localeCompare(b.label));
    },
    updatePrescriptionProgram(businessProgramId) {
      const copy = cloneDeep(this.value);
      copy.businessProgramId = businessProgramId;
      copy.medications = [];
      copy.rxItems = [];
      this.$emit("input", copy);
    },
    updatePrescriptionMedications(index, businessItemCategoryId) {
      const copy = cloneDeep(this.value);
      // Ensure the array has enough length to set the medication at the given index
      if (copy.medications.length < index + 1) copy.medications.length = index + 1;
      copy.medications[index] = businessItemCategoryId;
      this.$emit("input", copy);
    },
    updatePrescriptionNotes(val) {
      const copy = cloneDeep(this.value);
      copy.notes = val;
      this.$emit("input", copy);
    },
    onProgramGroupChange(val) {
      this.selectedProgramGroup = val;
      this.selectedProgramCategory = null; // Reset category when group changes
      this.filterPrograms();
      this.filterCategories();
      // Reset program and related selections when group changes
      this.$emit("input", {
        ...this.value,
        businessProgramId: null,
        medications: [],
        notes: null,
        rxItems: [],
      });
    },
    onProgramCategoryChange(val) {
      this.selectedProgramCategory = val;
      this.filterPrograms();
      // Reset program and related selections when category changes
      this.$emit("input", {
        ...this.value,
        businessProgramId: null,
        medications: [],
        notes: null,
        rxItems: [],
      });
    },
    filterPrograms() {
      let filtered = this.programs.flatMap((pg) => pg.options || []);
      if (this.selectedProgramGroup) filtered = filtered.filter((p) => p.programGroup === this.selectedProgramGroup);
      if (this.selectedProgramCategory) filtered = filtered.filter((p) => p.programCategory === this.selectedProgramCategory);
      this.filteredPrograms = filtered;
    },
    filterCategories() {
      const all = this.programs.flatMap((pg) => pg.options || []);
      let filtered = all;
      // If a group is selected, filter categories only for programs within that group
      if (this.selectedProgramGroup) {
        filtered = filtered.filter((p) => p.programGroup === this.selectedProgramGroup && p.programCategory);
      }
      // Get unique categories from the filtered programs
      this.programCategories = [
        ...new Map(filtered.filter((p) => p.programCategory).map((p) => [p.programCategory, { value: p.programCategory, label: p.programCategory }])).values(),
      ].sort((a, b) => a.label.localeCompare(b.label));
    },
    getGroups(programList) {
      const all = programList.flatMap((pg) => pg.options || []);
      // Extract unique program groups, excluding null values
      return [...new Map(all.map((item) => [item.programGroup, { value: item.programGroup, label: item.programGroup }])).values()].filter((x) => x.value !== null);
    },
    getCategories(programList) {
      const all = programList.flatMap((pg) => pg.options || []);
      // Extract unique program categories, filtering out those without a category
      return [...new Map(all.filter((opt) => opt.programCategory).map((opt) => [opt.programCategory, { value: opt.programCategory, label: opt.programCategory }])).values()].sort(
        (a, b) => a.label.localeCompare(b.label),
      );
    },
    formatFieldLabel(label) {
      if (!label) return "Medication";
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
  },
  watch: {
    programs: {
      handler() {
        this.initializeFilters();
        this.filterPrograms();
        this.filterCategories();
      },
      deep: true,
    },
  },
};
</script>
